/*
  Damano Produktseite
  - Farbe wählt die Bilder dieser Farbe. Ein Bild gehört zur Farbe, deren Variante es als Variantenbild hat,
    oder deren Name im Alt-Text steht; die folgenden Bilder gehören dazu bis zum nächsten Variantenbild.
    Bilder vor dem ersten Variantenbild gelten für alle Farben.
  - Ausverkaufte Grössen bleiben stehen, durchgestrichen und nicht wählbar.
  - Der Knopf heisst «Grösse wählen», bis eine Grösse gewählt ist, dann «In die Tasche».
  - Das Hinzufügen selbst macht Dawns <product-form> (öffnet die Tasche).
*/
if (!customElements.get('d-product')) {
  class DamanoProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.variants = this.data.variants;
      this.strings = this.data.strings;
      this.colorIndex = parseInt(this.dataset.colorIndex, 10);
      this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
      this.fieldsets = [...this.querySelectorAll('[data-option-index]')];
      this.selected = this.fieldsets.map((fs) => fs.querySelector('[aria-pressed="true"]')?.dataset.value ?? null);

      this.fieldsets.forEach((fs) => {
        const index = parseInt(fs.dataset.optionIndex, 10);
        fs.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-value]');
          if (!button || button.disabled) return;
          this.select(index, button.dataset.value);
        });
      });

      this.querySelectorAll('[data-cta]').forEach((cta) =>
        cta.addEventListener('click', (event) => {
          if (cta.getAttribute('aria-disabled') !== 'true') return;
          event.preventDefault();
          this.promptSize();
        })
      );

      this.querySelector('[data-open-fit]')?.addEventListener('click', () => {
        const fit = this.querySelector('[id^="Details-fit-"]');
        if (fit) fit.open = true;
      });

      this.setupGallery();

      if (this.querySelector('[data-sticky-cta]')) document.body.classList.add('d-has-sticky-cta');

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          if (!this.variants.some((v) => String(v.id) === String(event.productVariantId))) return;
          this.setNote(this.strings.inBag);
        });
      }

      this.update({ initial: true });
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      document.body.classList.remove('d-has-sticky-cta');
    }

    /* ---------- Auswahl ---------- */

    select(index, value) {
      this.selected[index] = value;
      if (index === this.colorIndex && this.sizeIndex >= 0) {
        // Grösse behalten, wenn sie in der neuen Farbe verfügbar ist
        const size = this.selected[this.sizeIndex];
        if (size !== null && !this.isAvailable(this.sizeIndex, size)) this.selected[this.sizeIndex] = null;
      }
      this.update();
    }

    matches(variant, selection, skipIndex) {
      return selection.every((value, i) => i === skipIndex || value === null || variant.options[i] === value);
    }

    // Gibt es eine verfügbare Variante mit diesem Wert, passend zu den übrigen Wahlen?
    isAvailable(index, value) {
      const selection = [...this.selected];
      if (index === this.colorIndex) {
        // Farbe: nur ob die Farbe überhaupt noch etwas hat
        return this.variants.some((v) => v.available && v.options[index] === value);
      }
      if (index !== this.sizeIndex && this.sizeIndex >= 0) selection[this.sizeIndex] = null;
      return this.variants.some((v) => v.available && v.options[index] === value && this.matches(v, selection, index));
    }

    get currentVariant() {
      if (this.selected.some((value) => value === null)) return null;
      return this.variants.find((v) => this.matches(v, this.selected)) || null;
    }

    get sizeMissing() {
      return this.sizeIndex >= 0 && this.selected[this.sizeIndex] === null;
    }

    /* ---------- Darstellung ---------- */

    update({ initial = false } = {}) {
      this.fieldsets.forEach((fs) => {
        const index = parseInt(fs.dataset.optionIndex, 10);
        fs.querySelectorAll('button[data-value]').forEach((button) => {
          const value = button.dataset.value;
          const pressed = this.selected[index] === value;
          const available = this.isAvailable(index, value);
          button.setAttribute('aria-pressed', String(pressed));
          if (index === this.colorIndex) {
            button.classList.toggle('is-unavailable', !available);
          } else {
            button.disabled = !available;
            button.classList.toggle('is-unavailable', !available);
          }
        });
        const label = fs.querySelector('[data-option-value]');
        if (label) label.textContent = this.selected[index] ?? '';
      });

      const variant = this.currentVariant;
      const reference =
        variant ||
        this.variants.find((v) => this.matches(v, this.selected) && v.available) ||
        this.variants.find((v) => this.matches(v, this.selected)) ||
        this.variants[0];

      // Preis
      const price = this.querySelector('[data-price]');
      if (price && reference && !initial) price.innerHTML = reference.priceHtml;

      // Formular und Knöpfe
      const input = this.closest('product-component')?.querySelector('[data-variant-id-input]') || this.querySelector('[data-variant-id-input]');
      if (input && variant) input.value = variant.id;

      let label = this.strings.addToCart;
      let disabled = false;
      if (this.sizeMissing) {
        label = this.strings.chooseSize;
        disabled = true;
      } else if (!variant || !variant.available) {
        label = this.strings.soldOut;
        disabled = true;
      }
      this.querySelectorAll('[data-cta]').forEach((cta) => {
        cta.setAttribute('aria-disabled', String(disabled));
        cta.classList.toggle('is-choose', this.sizeMissing);
        const span = cta.querySelector('[data-cta-label]');
        if (span) span.textContent = label;
      });

      // Hinweis zum Lager
      if (!initial || this.sizeIndex < 0 || !this.sizeMissing) {
        if (this.sizeMissing) this.setNote(this.strings.chooseSizeNote);
        else if (variant && variant.available && variant.tracked && variant.qty === 1) this.setNote(this.strings.lastPiece);
        else if (variant && variant.available) this.setNote(this.strings.inStock);
        else this.setNote(variant ? this.strings.soldOut : '');
      }

      // Anfrage-E-Mail mit Farbe und Grösse
      this.querySelectorAll('[data-try-on]').forEach((link) => {
        const details = this.selected.filter((v) => v !== null).join(', ');
        const body = details ? this.strings.mailBody.replace('[details]', details) + `\n${window.location.origin}${this.dataset.url}` : '';
        link.href = `mailto:${this.data.email}?subject=${encodeURIComponent(link.dataset.subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}`;
      });

      // Bilder
      if (this.colorIndex >= 0) this.filterGallery(this.selected[this.colorIndex], initial);

      // Adresse
      if (!initial && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    setNote(text) {
      this.querySelectorAll('[data-stock-note]').forEach((note) => (note.textContent = text));
    }

    promptSize() {
      const group = this.fieldsets.find((fs) => parseInt(fs.dataset.optionIndex, 10) === this.sizeIndex);
      if (!group) return;
      this.setNote(this.strings.chooseSizeNote);
      group.classList.add('is-prompted');
      setTimeout(() => group.classList.remove('is-prompted'), 1200);
      group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      group.querySelector('button:not([disabled])')?.focus({ preventScroll: true });
    }

    /* ---------- Galerie ---------- */

    setupGallery() {
      this.galleryList = this.querySelector('[data-gallery-list]');
      this.dots = this.querySelector('[data-gallery-dots]');
      if (!this.galleryList) return;
      this.items = [...this.galleryList.querySelectorAll('.d-gallery__item')];
      this.galleryList.addEventListener('scroll', () => this.updateDots(), { passive: true });
      if (this.colorIndex < 0) this.markVisible(this.items);
    }

    filterGallery(color, initial) {
      if (!this.items) return;
      const matching = this.items.filter((item) => item.dataset.mediaColor === color);
      const visible = matching.length
        ? this.items.filter((item) => item.dataset.mediaColor === color || item.dataset.mediaColor === '')
        : this.items;
      // Bilder der gewählten Farbe zuerst
      const ordered = matching.length ? [...matching, ...visible.filter((item) => !matching.includes(item))] : visible;
      this.items.forEach((item) => (item.hidden = !visible.includes(item)));
      ordered.forEach((item) => this.galleryList.appendChild(item));
      this.markVisible(ordered);
      if (!initial) this.galleryList.scrollTo({ left: 0 });
    }

    markVisible(visible) {
      this.visibleItems = visible;
      this.items.forEach((item) => item.classList.remove('is-first'));
      if (visible[0]) {
        visible[0].classList.add('is-first');
        const img = visible[0].querySelector('img');
        if (img) img.loading = 'eager';
      }
      if (this.dots) {
        this.dots.innerHTML = visible.length > 1 ? visible.map(() => '<span></span>').join('') : '';
        this.updateDots();
      }
    }

    updateDots() {
      if (!this.dots || !this.visibleItems) return;
      const width = this.galleryList.clientWidth || 1;
      const index = Math.round(this.galleryList.scrollLeft / width);
      [...this.dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
  }

  customElements.define('d-product', DamanoProduct);
}
